import React from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { getTokens } from "../../styles/tokens";
import { resolveTone } from "./StatusBadge";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    root: {
      display: "flex",
      alignItems: "flex-start",
      gap: 14,
      padding: "20px 18px",
      height: "100%",
      boxSizing: "border-box",
      backgroundColor: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.card,
      minWidth: 0,
      position: "relative",
      overflow: "hidden",
      transition: `border-color ${t.motion.normal}, box-shadow ${t.motion.normal}`,
    },
    clickable: {
      cursor: "pointer",
      "&:hover": {
        borderColor: t.tint.strong,
        boxShadow: t.shadow.soft,
      },
      "&:focus-visible": {
        outline: "none",
        boxShadow: `${t.shadow.glow}, ${t.shadow.soft}`,
      },
    },
    iconBox: {
      flex: "none",
      width: 43,
      height: 43,
      borderRadius: t.radius.sm,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& .MuiSvgIcon-root": { fontSize: 22 },
      "& svg": { width: 22, height: 22 },
    },
    body: { minWidth: 0, flex: 1 },
    label: {
      fontFamily: t.font,
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 1.4,
      color: t.textSecondary,
      margin: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    valueRow: {
      display: "flex",
      alignItems: "baseline",
      gap: 8,
      minWidth: 0,
      marginTop: 6,
    },
    value: {
      fontFamily: t.font,
      fontSize: 30,
      fontWeight: 600,
      // 1.1 e não 1: com `overflow: hidden` a linha exata corta descendentes
      lineHeight: 1.1,
      letterSpacing: "-1px",
      color: t.text,
      fontVariantNumeric: "tabular-nums",
      margin: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    // valores compostos ("1279/11500") não cabem a 30px em 4 colunas
    valueLong: {
      fontSize: 24,
      letterSpacing: "-0.5px",
    },
    hint: {
      fontFamily: t.font,
      fontSize: 12,
      color: t.textMuted,
      margin: "8px 0 0",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    trend: {
      fontFamily: t.font,
      fontSize: 12,
      fontWeight: 500,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
    },
  };
});

const formatTrend = (trend) => {
  if (trend == null) return null;
  if (typeof trend === "object") {
    const v = trend.value;
    const dir = trend.direction || (typeof v === "number" ? (v > 0 ? "up" : v < 0 ? "down" : "flat") : "flat");
    const text = trend.label != null ? trend.label : typeof v === "number" ? `${v > 0 ? "+" : ""}${v}%` : String(v);
    return { dir, text, positive: trend.positive };
  }
  if (typeof trend === "number") {
    return { dir: trend > 0 ? "up" : trend < 0 ? "down" : "flat", text: `${trend > 0 ? "+" : ""}${trend}%` };
  }
  return { dir: "flat", text: String(trend) };
};

/**
 * Cartão de métrica do painel: quadro de ícone 43px, rótulo, número 30px/600 e detalhe.
 *
 * Props: `label`, `value`, `icon?`, `tone?` (`brand|success|warning|error|info|neutral`, default `brand`),
 * `hint?` (texto 12px ink-400 abaixo do valor), `trend?` (número → "+12%"; ou
 * `{ value, label?, direction?: 'up'|'down'|'flat', positive?: boolean }`), `onClick?`.
 * Aliases aceitos: `title` (= label), `accent` (= tone).
 */
const StatCard = ({ label, title, value, icon, tone, accent, hint, trend, onClick, className, style, ...rest }) => {
  const classes = useStyles();
  const theme = useTheme();
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const colors = resolveTone(t, tone || accent || "brand", theme.palette.type);
  const tr = formatTrend(trend);

  let trendColor = t.textMuted;
  if (tr) {
    const good = tr.positive != null ? tr.positive : tr.dir === "up";
    if (tr.dir !== "flat") {
      trendColor = resolveTone(t, good ? "success" : "error", theme.palette.type).fg;
    }
  }
  const arrow = tr ? (tr.dir === "up" ? "▲" : tr.dir === "down" ? "▼" : "") : "";
  const isLongValue =
    (typeof value === "string" || typeof value === "number") && String(value).length > 8;

  return (
    <div
      className={[classes.root, onClick ? classes.clickable : null, className].filter(Boolean).join(" ")}
      style={style}
      onClick={onClick}
      onKeyDown={onClick ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(event);
        }
      } : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      {...rest}
    >
      {icon && (
        <div className={classes.iconBox} style={{ backgroundColor: colors.bg, color: colors.fg }}>
          {icon}
        </div>
      )}
      <div className={classes.body}>
        <p className={classes.label}>{label != null ? label : title}</p>
        <div className={classes.valueRow}>
          <p className={[classes.value, isLongValue ? classes.valueLong : null].filter(Boolean).join(" ")}>{value}</p>
          {tr && (
            <span className={classes.trend} style={{ color: trendColor }}>
              {arrow ? `${arrow} ` : ""}
              {tr.text}
            </span>
          )}
        </div>
        {hint && <p className={classes.hint}>{hint}</p>}
      </div>
    </div>
  );
};

export default StatCard;
